const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-950 py-16 px-4">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-12">
          <div className="text-blue-400 font-mono text-sm mb-3">
            // Legal
          </div>
          <h1 className="text-4xl font-bold text-blue-200 mb-3">
            Privacy Policy
          </h1>
          <p className="text-gray-500 text-sm">
            Last updated: May 9, 2026
          </p>
        </div>

        <div className="space-y-10 text-gray-400 leading-relaxed">

          {/* Intro */}
          <section>
            <p className="text-gray-300">
              ScaffoldBridge ("we", "our", or "us") is committed to
              protecting your privacy. This Privacy Policy explains how
              we collect, use, and safeguard your information when you
              use our platform at scaffoldbridge.io.
            </p>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              1. Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Account Information
                </h3>
                <p>
                  When you create an account, we collect your name,
                  email address, and profile picture via Auth0
                  authentication.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Schema Data
                </h3>
                <p>
                  We store the SQL schemas and OpenAPI specifications
                  you submit for code generation. We store only the
                  structure of your schema — never your actual database
                  data or business records.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Usage Data
                </h3>
                <p>
                  We collect information about how you use ScaffoldBridge,
                  including generation count, features used, and
                  timestamps.
                </p>
              </div>
              <div>
                <h3 className="text-gray-300 font-medium mb-2">
                  Payment Information
                </h3>
                <p>
                  Payment processing is handled by Stripe. We do not
                  store your credit card details. We only store your
                  Stripe customer ID and subscription status.
                </p>
              </div>
            </div>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              2. How We Use Your Information
            </h2>
            <ul className="space-y-2">
              {[
                'To provide and improve the ScaffoldBridge service',
                'To process payments and manage your subscription',
                'To send important service notifications',
                'To enforce our Terms of Service',
                'To respond to your support requests',
                'To analyze usage patterns and improve features',
              ].map(item => (
                <li key={item}
                    className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1 shrink-0">→</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              3. Data Sharing
            </h2>
            <p className="mb-4">
              We do not sell, trade, or rent your personal information
              to third parties. We share data only with:
            </p>
            <div className="space-y-3">
              {[
                {
                  name: 'Auth0',
                  desc: 'Authentication provider — stores your login credentials'
                },
                {
                  name: 'Stripe',
                  desc: 'Payment processor — handles subscription billing'
                },
                {
                  name: 'Supabase',
                  desc: 'Database provider — stores your account and schema data'
                },
                {
                  name: 'Railway',
                  desc: 'Hosting provider — runs our backend API'
                },
                {
                  name: 'Vercel',
                  desc: 'Hosting provider — serves our frontend application'
                },
              ].map(item => (
                <div key={item.name}
                     className="bg-gray-900 border border-gray-800
                                rounded-xl p-4">
                  <span className="text-gray-300 font-medium">
                    {item.name}
                  </span>
                  <span className="text-gray-500 ml-2">
                    — {item.desc}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm">
              All providers above are SOC 2 certified and handle
              your data securely.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              4. Data Security
            </h2>
            <ul className="space-y-2">
              {[
                'All data is transmitted over HTTPS/TLS encryption',
                'Database access is restricted and monitored',
                'We use UUIDs for server identifiers to prevent enumeration',
                'Sensitive values are encrypted at rest',
                'We conduct regular security reviews',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-green-500 mt-1 shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              5. Your Rights
            </h2>
            <p className="mb-4">
              You have the following rights regarding your data:
            </p>
            <div className="space-y-3">
              {[
                {
                  right: 'Access',
                  desc: 'Request a copy of all data we hold about you'
                },
                {
                  right: 'Correction',
                  desc: 'Request correction of inaccurate data'
                },
                {
                  right: 'Deletion',
                  desc: 'Delete your account and all associated data permanently'
                },
                {
                  right: 'Portability',
                  desc: 'Export your schemas and account data in JSON format'
                },
                {
                  right: 'Objection',
                  desc: 'Object to processing of your data for marketing'
                },
              ].map(item => (
                <div key={item.right}
                     className="flex items-start gap-3">
                  <span className="text-blue-400 font-semibold
                                   text-sm w-24 shrink-0 pt-0.5">
                    {item.right}
                  </span>
                  <span>{item.desc}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm">
              To exercise any of these rights, email us at{' '}
              <a href="mailto:support@scaffoldbridge.io"
                 className="text-blue-400 hover:text-blue-300">
                support@scaffoldbridge.io
              </a>
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              6. Cookies
            </h2>
            <p className="mb-3">
              We use essential cookies only:
            </p>
            <ul className="space-y-2">
              {[
                'Authentication session cookies (required for login)',
                'Preference cookies (remember your settings)',
              ].map(item => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1 shrink-0">→</span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm">
              We do not use tracking, advertising, or analytics
              cookies without your consent.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              7. Data Retention
            </h2>
            <p>
              We retain your data for as long as your account is active.
              If you delete your account, we permanently delete all your
              data within 30 days. Billing records may be retained for
              up to 7 years as required by law.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              8. GDPR & CCPA
            </h2>
            <p className="mb-3">
              If you are in the European Union or California, you have
              additional rights under GDPR and CCPA respectively.
            </p>
            <p className="mb-3">
              We do not sell your personal information to third parties.
              If you are a California resident and wish to exercise your
              CCPA rights, contact us at{' '}
              <a href="mailto:support@scaffoldbridge.io"
                 className="text-blue-400 hover:text-blue-300">
                support@scaffoldbridge.io
              </a>
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              9. Children's Privacy
            </h2>
            <p>
              ScaffoldBridge is not intended for children under 13.
              We do not knowingly collect personal information from
              children under 13.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time.
              We will notify you of significant changes via email
              or a notice on our platform. Continued use of
              ScaffoldBridge after changes constitutes acceptance
              of the new policy.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-900 border border-gray-800
                              rounded-2xl p-6">
            <h2 className="text-blue-200 font-semibold text-xl mb-4">
              Contact Us
            </h2>
            <p className="mb-3">
              For privacy questions or to exercise your rights:
            </p>
            <div className="space-y-2 text-sm">
              <p>
                <span className="text-gray-300 font-medium">Email: </span>
                <a href="mailto:support@scaffoldbridge.io"
                   className="text-blue-400 hover:text-blue-300">
                  support@scaffoldbridge.io
                </a>
              </p>
              <p>
                <span className="text-gray-300 font-medium">Website: </span>
                <a href="https://scaffoldbridge.io"
                   className="text-blue-400 hover:text-blue-300">
                  scaffoldbridge.io
                </a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;